import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Search, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import { Submission } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';

export default function CorporateSubmissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      const response = await api.get('/submissions');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    }
  };

  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch =
      submission.invitation?.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.invitation?.kolProfile?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || submission.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingReview = submissions.filter((s) => s.status === 'SUBMITTED');

  const statusOptions = [
    { value: 'ALL', label: 'All Status' },
    { value: 'SUBMITTED', label: 'Pending Review' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REVISION_REQUESTED', label: 'Revision Requested' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUBMITTED':
        return <Badge variant="warning">Pending Review</Badge>;
      case 'APPROVED':
        return <Badge variant="success">Approved</Badge>;
      case 'REVISION_REQUESTED':
        return <Badge variant="destructive">Revision Requested</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const toggleSelectId = (submissionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(submissionId)) next.delete(submissionId);
      else next.add(submissionId);
      return next;
    });
  };

  const selectAllOnPage = () => {
    setSelectedIds(new Set(filteredSubmissions.map((s) => s.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Approve ${selectedIds.size} submission(s)?`)) return;
    setBulkApproving(true);
    try {
      for (const id of selectedIds) {
        await api.put(`/submissions/${id}`, { status: 'APPROVED' });
      }
      clearSelection();
      await loadSubmissions();
    } catch (error) {
      console.error('Bulk approve failed:', error);
      alert('Some submissions could not be approved. Refresh and try again.');
      await loadSubmissions();
    } finally {
      setBulkApproving(false);
    }
  };

  const columns = [
    {
      header: '',
      className: 'w-12',
      accessor: (row: Submission) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectId(row.id);
          }}
          className="rounded border-gray-300"
        />
      ),
    },
    {
      header: 'Campaign',
      accessor: (row: Submission) => (
        <div className="flex items-center gap-2 min-w-0">
          <Video className="w-5 h-5 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {row.invitation?.job?.title || 'Untitled Campaign'}
            </div>
            <div className="text-sm text-gray-600 truncate">
              By {row.invitation?.kolProfile?.name || 'Unknown KOL'} • Version {row.version}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: 'Caption',
      className: 'max-w-xs',
      accessor: (row: Submission) => (
        <span className="text-gray-600 line-clamp-2 whitespace-normal">
          {row.captionText || '—'}
        </span>
      ),
    },
    {
      header: 'Media',
      accessor: (row: Submission) =>
        row.media && row.media.length > 0
          ? `${row.media.length} file${row.media.length !== 1 ? 's' : ''}`
          : '—',
    },
    {
      header: 'Submitted',
      accessor: (row: Submission) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      header: 'Status',
      accessor: (row: Submission) => getStatusBadge(row.status),
    },
  ];

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-title">Content Submissions</h1>
          <p className="text-glass-text-2 text-sm mt-1">Review and approve final content</p>
        </div>
      </div>

      {/* Stats */}
      {pendingReview.length > 0 && (
        <Card className="border-[rgb(var(--warn)/0.35)] bg-[rgb(var(--warn)/0.1)]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">
                  {pendingReview.length} submission{pendingReview.length !== 1 ? 's' : ''} waiting for your review
                </p>
                <p className="text-sm text-orange-700">Please review and provide feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by campaign or KOL name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Submissions List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {`${filteredSubmissions.length} Submission${filteredSubmissions.length !== 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 p-3 glass-card-tight border-[rgb(var(--warn)/0.35)] text-sm">
              <span className="font-medium text-amber-900">{selectedIds.size} selected</span>
              <Button size="sm" variant="outline" onClick={selectAllOnPage}>
                Select all on page
              </Button>
              <Button size="sm" variant="outline" onClick={clearSelection}>
                Clear selection
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-green-200 text-green-800"
                onClick={handleBulkApprove}
                disabled={bulkApproving}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {bulkApproving ? 'Approving…' : 'Bulk Approve'}
              </Button>
            </div>
          )}

          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'No submissions match your filters'
                  : 'No content submitted yet'}
              </p>
              {!searchTerm && statusFilter === 'ALL' && (
                <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                  No content has been submitted for your campaigns yet. KOLs will submit content once their scripts are approved.
                </p>
              )}
            </div>
          ) : (
            <DataTable
              data={filteredSubmissions}
              columns={columns}
              onRowClick={(row) => navigate(`/brand/submissions/${toSlug(row.invitation?.job?.title || 'submission', row.id)}`)}
              emptyMessage="No submissions match your filters"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
